import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  CurecallVariableButton,
  CurecallTag,
  CurecallButton,
  ECurecallButtonAppearance,
  ECurecallSize,
  TrashIcon,
} from '@honestica/curecall-ui'
import { TNodeAction } from '../../node.type'
import { useProjectMedAlerts } from '../use-project-med-alerts.hook'
import { MED_ALERT_UI_TO_TAG_STATUS, TMedAlert } from 'src/types/med-alert'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { useLocale } from 'src/hooks/use-locale'
import { SelectOrAddMedAlertModal } from '../med-alert/select-or-add-medalert-modal.component'

interface MedAlertSectionProps {
  actionPath: string
  onUpdate: (action: Partial<TNodeAction>) => void
  responseContext?: 'yes' | 'no'
}

export function MedAlertSection({ actionPath, onUpdate, responseContext }: MedAlertSectionProps) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { watch, setValue } = useFormContext()
  const [isMedAlertModalOpen, setIsMedAlertModalOpen] = useState(false)
  const { data: medAlerts = [] } = useProjectMedAlerts()

  const action = watch(actionPath) as TNodeAction | undefined
  const medAlertId = action?.medAlertId
  const selectedMedAlert = medAlerts.find((alert: TMedAlert) => alert.id === medAlertId)

  const handleAddMedAlert = (newMedAlertId: number) => {
    setValue(`${actionPath}.medAlertId`, newMedAlertId, { shouldDirty: true })
    onUpdate({ medAlertId: newMedAlertId })
    setIsMedAlertModalOpen(false)
  }

  const handleRemoveMedAlert = () => {
    setValue(`${actionPath}.medAlertId`, undefined, { shouldDirty: true })
    onUpdate({ medAlertId: undefined })
  }

  if (selectedMedAlert) {
    return (
      <div className="c-flex c-items-center c-gap-1">
        <CurecallTag status={MED_ALERT_UI_TO_TAG_STATUS[selectedMedAlert.ui]}>
          {getTranslatedLabel(locale, selectedMedAlert.intlLabel)}
        </CurecallTag>
        <CurecallButton
          iconType={<TrashIcon />}
          isGhostButton
          onClick={handleRemoveMedAlert}
          appearance={ECurecallButtonAppearance.CANCEL}
          size={ECurecallSize.SMALL}
          ariaLabelText={t('common.actions.delete')}
        />
      </div>
    )
  }

  return (
    <>
      <CurecallVariableButton isAddingButton={true} onClick={() => setIsMedAlertModalOpen(true)}>
        {t('pages.configuration.followups.chatbot.conditional-sections.add-medalert')}
      </CurecallVariableButton>

      <SelectOrAddMedAlertModal
        isOpen={isMedAlertModalOpen}
        onCancel={() => setIsMedAlertModalOpen(false)}
        onValidate={handleAddMedAlert}
        responseContext={responseContext}
      />
    </>
  )
}
